import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Crown, Search, ChevronDown, Trophy, Wallet, History,
  Plus, Minus, RotateCcw, Loader2, X, Save, ShieldCheck, TrendingUp, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const FEATURE_LABEL = {
  generate_bio: "Artist bio",
  generate_caption: "Social caption",
  generate_social_post: "Social media post",
  generate_hashtags: "Hashtags",
  generate_dj_script: "AI DJ script",
  generate_news_bulletin: "News bulletin",
  generate_podcast: "Podcast meta",
  talk_show_to_podcast: "Talk show → podcast notes",
  generate_campaign_pack: "Full social campaign",
  translate_content: "Translation",
  generate_commercial: "Commercial script",
  imaging_generate: "Imaging / image gen",
  transcribe: "Transcription",
  stream_bio: "Streaming bio",
  generate_cover: "Cover art",
  blog_post: "Blog post",
  snippet_transcribe: "Snippet Studio — transcription",
  snippet_highlights: "Snippet Studio — highlight detection",
  snippet_clip_render: "Snippet Studio — video render",
  snippet_captions: "Snippet Studio — captions & hashtags",
  snippet_video_fx: "Snippet Studio — premium video FX",
  rip_identify_ai: "Rip Studio — AI song identification",
  ai_compose: "AI auto-compose helper",
  ai_ideate: "AI ideate helper",
  ai_default: "Default AI cost",
};

const isPrivilegedRole = (r) => ["super_admin", "sysadmin", "owner"].includes(r);

export default function AdminCredits() {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");

  if (!user) return null;
  if (!isPrivilegedRole(user.role)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="admin-credits-page">
      <PageHeader
        eyebrow="// ADMIN · CREDITS"
        title="Manage every customer's credit wallet."
        description="Adjust balances, view history, change per-feature costs, and see who's burning the most credits."
      />

      <div className="flex gap-1 mb-6 border-b border-line" data-testid="admin-tabs">
        <Tab id="users" active={tab} onClick={setTab} icon={Wallet}>All wallets</Tab>
        <Tab id="top" active={tab} onClick={setTab} icon={Trophy}>Top spenders</Tab>
        <Tab id="costs" active={tab} onClick={setTab} icon={ShieldCheck}>Feature costs</Tab>
        <Tab id="nudge" active={tab} onClick={setTab} icon={TrendingUp}>Annual nudge</Tab>
        <Tab id="gateways" active={tab} onClick={setTab} icon={CreditCard}>Gateways</Tab>
      </div>

      {tab === "users" && <UsersTab />}
      {tab === "top" && <LeaderboardTab />}
      {tab === "costs" && <CostsTab />}
      {tab === "nudge" && <NudgeTab />}
      {tab === "gateways" && <GatewaysTab />}
    </div>
  );
}

function GatewaysTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/gateway-analytics").then((r) => setData(r.data))
      .catch(() => toast.error("Could not load gateway analytics"));
  }, []);

  if (!data) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin text-brand-amber mx-auto" /></div>;

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="admin-gateways">
      <div className="flex items-baseline justify-between mb-5">
        <h3 className="font-display text-lg font-bold">Payment gateway performance</h3>
        <div className="text-sm text-ink-secondary" data-testid="gateway-total-revenue">
          Total paid revenue: <span className="text-brand-amber font-bold">${data.total_revenue.toLocaleString()}</span>
        </div>
      </div>
      {data.gateways.length === 0 ? (
        <div className="text-sm text-ink-muted py-8 text-center" data-testid="gateway-empty">No payment attempts yet.</div>
      ) : (
        <table className="w-full text-sm" data-testid="gateway-table">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-muted border-b border-line">
              <th className="py-2 pr-3">Gateway</th>
              <th className="py-2 pr-3">Paid / Attempts</th>
              <th className="py-2 pr-3">Conversion</th>
              <th className="py-2 pr-3">Revenue</th>
              <th className="py-2 pr-3 w-[30%]">Revenue share</th>
              <th className="py-2">Last payment</th>
            </tr>
          </thead>
          <tbody>
            {data.gateways.map((g) => (
              <tr key={g.provider} className="border-b border-line/30" data-testid={`gateway-row-${g.provider}`}>
                <td className="py-2.5 pr-3 font-semibold capitalize">{g.provider}</td>
                <td className="py-2.5 pr-3">{g.paid} / {g.attempts}</td>
                <td className="py-2.5 pr-3">{g.conversion}%</td>
                <td className="py-2.5 pr-3 text-brand-amber font-semibold">${g.revenue.toLocaleString()}</td>
                <td className="py-2.5 pr-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-bg-elevated overflow-hidden">
                      <div className="h-full rounded-full bg-brand-amber" style={{ width: `${g.share}%` }} />
                    </div>
                    <span className="text-xs text-ink-muted w-9 text-right">{g.share}%</span>
                  </div>
                </td>
                <td className="py-2.5 text-xs text-ink-muted">
                  {g.last_paid ? new Date(g.last_paid).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Tab({ id, active, onClick, icon: Icon, children }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`admin-tab-${id}`}
      className={`relative px-4 py-2.5 text-sm font-mono uppercase tracking-wider inline-flex items-center gap-2 transition ${
        isActive ? "text-brand-amber" : "text-ink-muted hover:text-ink-primary"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
      {isActive && <span className="absolute left-2 right-2 bottom-0 h-[2px] bg-brand-amber" />}
    </button>
  );
}

// ────────────────────────────────────────────────────────────
// Wallets tab
// ────────────────────────────────────────────────────────────
function UsersTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjust, setAdjust] = useState(null);   // { user, mode: 'grant'|'revoke' }
  const [history, setHistory] = useState(null); // { user, rows }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/credits/users", { params: { q, limit: 100 } });
      setRows(r.data.users || []);
    } catch {
      toast.error("Could not load wallets");
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, or user_id..."
            data-testid="admin-user-search"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-line bg-bg-elevated text-sm focus:border-brand-amber outline-none transition"
          />
        </div>
        <span className="text-xs text-ink-muted font-mono">{rows.length} wallets</span>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-muted">
            <tr className="border-b border-line">
              <th className="p-3">User</th>
              <th className="p-3">Plan</th>
              <th className="p-3 text-right">Monthly</th>
              <th className="p-3 text-right">Top-up</th>
              <th className="p-3 text-right">Lifetime</th>
              <th className="p-3 text-right">Period ends</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-8 text-center text-xs font-mono text-ink-muted">LOADING...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-sm text-ink-muted">No wallets match.</td></tr>
            )}
            {rows.map((u) => {
              const role = u.role;
              return (
                <tr key={u.user_id} className="border-b border-line/30 hover:bg-bg-elevated/40" data-testid={`admin-row-${u.user_id}`}>
                  <td className="p-3">
                    <div className="font-medium text-ink-primary">{u.name || "—"}</div>
                    <div className="text-xs text-ink-muted font-mono">{u.email || u.user_id}</div>
                    {isPrivilegedRole(role) && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider rounded-full bg-violet-500/15 text-violet-300 px-1.5 py-0.5 mt-1">
                        <Crown className="h-2.5 w-2.5" /> {role?.replace("_", " ")}
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-bg-elevated text-brand-amber">
                      {(u.plan || "free").replace("_", " ")}
                    </span>
                  </td>
                  <td className="p-3 text-right font-mono">
                    <span className={u.monthly_remaining < (0.1 * (u.monthly_allowance || 1)) ? "text-rose-300" : ""}>
                      {u.monthly_remaining}
                    </span>
                    <span className="text-ink-muted"> / {u.monthly_allowance}</span>
                  </td>
                  <td className="p-3 text-right font-mono text-brand-amber">{u.topup_balance}</td>
                  <td className="p-3 text-right font-mono text-ink-secondary">{u.lifetime_used}</td>
                  <td className="p-3 text-right text-xs font-mono text-ink-muted">
                    {u.period_end ? new Date(u.period_end).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <IconBtn label="Grant" icon={Plus} testid={`admin-grant-${u.user_id}`}
                               onClick={() => setAdjust({ user: u, mode: "grant" })} />
                      <IconBtn label="Revoke" icon={Minus} testid={`admin-revoke-${u.user_id}`}
                               onClick={() => setAdjust({ user: u, mode: "revoke" })} tone="rose" />
                      <IconBtn label="History" icon={History} testid={`admin-history-${u.user_id}`}
                               onClick={() => setHistory({ user: u, rows: null })} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {adjust && (
        <AdjustModal
          target={adjust}
          onClose={() => setAdjust(null)}
          onDone={() => { setAdjust(null); load(); }}
        />
      )}
      {history && (
        <HistoryModal target={history} onClose={() => setHistory(null)} />
      )}
    </>
  );
}

function IconBtn({ icon: Icon, label, onClick, testid, tone = "neutral" }) {
  const toneCls = {
    neutral: "border-line bg-bg-elevated hover:text-brand-amber",
    rose: "border-rose-500/20 bg-rose-500/5 hover:text-rose-300",
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={label}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition ${toneCls}`}
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function AdjustModal({ target, onClose, onDone }) {
  const { user, mode } = target;
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (amount <= 0) return toast.error("Amount must be > 0");
    setSaving(true);
    try {
      const endpoint = mode === "grant" ? "/admin/credits/grant" : "/admin/credits/revoke";
      await api.post(endpoint, { user_id: user.user_id, amount, reason });
      toast.success(`${mode === "grant" ? "Granted" : "Revoked"} ${amount} credits`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="admin-adjust-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-line bg-bg-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-xl font-bold flex items-center gap-2">
            {mode === "grant" ? <Plus className="h-5 w-5 text-emerald-400" /> : <Minus className="h-5 w-5 text-rose-400" />}
            {mode === "grant" ? "Grant credits" : "Revoke credits"}
          </div>
          <button onClick={onClose}><X className="h-4 w-4 text-ink-muted" /></button>
        </div>
        <div className="text-sm text-ink-secondary mb-1">{user.name || user.email}</div>
        <div className="text-xs font-mono text-ink-muted mb-4">
          Current: top-up <span className="text-brand-amber">{user.topup_balance}</span>
          · monthly <span className="text-ink-primary">{user.monthly_remaining}/{user.monthly_allowance}</span>
        </div>
        <label className="block text-xs font-mono uppercase tracking-wider text-ink-muted mb-1">Amount</label>
        <input
          type="number"
          value={amount}
          min={1}
          onChange={(e) => setAmount(parseInt(e.target.value || "0", 10))}
          data-testid="admin-adjust-amount"
          className="w-full mb-3 px-3 py-2 rounded-md border border-line bg-bg-elevated text-sm focus:border-brand-amber outline-none"
        />
        <label className="block text-xs font-mono uppercase tracking-wider text-ink-muted mb-1">Reason (optional)</label>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., goodwill credit / support ticket #1234"
          data-testid="admin-adjust-reason"
          className="w-full mb-4 px-3 py-2 rounded-md border border-line bg-bg-elevated text-sm focus:border-brand-amber outline-none"
        />
        <button
          onClick={submit}
          disabled={saving}
          data-testid="admin-adjust-submit"
          className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition ${
            mode === "grant"
              ? "bg-emerald-500 text-white hover:bg-emerald-600"
              : "bg-rose-500 text-white hover:bg-rose-600"
          } disabled:opacity-50`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "grant" ? <Plus className="h-4 w-4" /> : <Minus className="h-4 w-4" />)}
          {mode === "grant" ? "Grant" : "Revoke"} {amount} credits
        </button>
      </div>
    </div>
  );
}

function HistoryModal({ target, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/credits/user/${target.user.user_id}/history?limit=100`);
        setRows(r.data.items || []);
      } catch {
        setRows([]);
        toast.error("Could not load history");
      }
    })();
  }, [target.user.user_id]);

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="admin-history-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[80vh] rounded-2xl border border-line bg-bg-surface p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="font-display text-xl font-bold">{target.user.name || target.user.email}</div>
          <button onClick={onClose}><X className="h-4 w-4 text-ink-muted" /></button>
        </div>
        <div className="text-xs text-ink-muted font-mono mb-4">Credit ledger history</div>

        {rows === null ? (
          <div className="py-10 text-center font-mono text-xs text-ink-muted">LOADING...</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-ink-muted">No history yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-muted">
              <tr className="border-b border-line">
                <th className="py-2">Date</th>
                <th>Feature</th>
                <th>Kind</th>
                <th>Reason</th>
                <th className="text-right">Δ</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h, i) => {
                const isSpend = (h.credits || 0) < 0;
                return (
                  <tr key={i} className="border-b border-line/30">
                    <td className="py-2 text-xs font-mono text-ink-muted whitespace-nowrap">
                      {new Date(h.created_at).toLocaleString()}
                    </td>
                    <td>{FEATURE_LABEL[h.feature] || h.feature}</td>
                    <td className="text-xs font-mono uppercase">{h.kind}</td>
                    <td className="text-xs text-ink-muted">{h.reason || ""}</td>
                    <td className={`text-right font-mono ${isSpend ? "text-rose-400" : "text-emerald-400"}`}>
                      {isSpend ? "" : "+"}{h.credits}
                    </td>
                    <td className="text-right font-mono">{h.balance_after}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Leaderboard tab
// ────────────────────────────────────────────────────────────
function LeaderboardTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/credits/leaderboard?limit=25");
        setRows(r.data.top_spenders || []);
      } catch { toast.error("Could not load leaderboard"); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="admin-leaderboard">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="h-5 w-5 text-brand-amber" />
        <h3 className="font-display text-xl font-bold">Top spenders · lifetime</h3>
      </div>
      {loading ? (
        <div className="py-10 text-center font-mono text-xs text-ink-muted">LOADING...</div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-ink-muted">No spend recorded yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-muted">
            <tr className="border-b border-line">
              <th className="py-2 w-10">#</th>
              <th>User</th>
              <th>Plan</th>
              <th className="text-right">This month</th>
              <th className="text-right">Lifetime</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u, i) => (
              <tr key={u.user_id} className="border-b border-line/30">
                <td className="py-2 font-display font-bold text-brand-amber">{i + 1}</td>
                <td>
                  <div className="text-ink-primary font-medium">{u.name || "—"}</div>
                  <div className="text-xs text-ink-muted font-mono">{u.email}</div>
                </td>
                <td className="text-xs font-mono uppercase">{(u.plan || "free").replace("_", " ")}</td>
                <td className="text-right font-mono">{u.this_month_used}</td>
                <td className="text-right font-mono text-brand-amber">{u.lifetime_used}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Per-feature cost editor
// ────────────────────────────────────────────────────────────
function CostsTab() {
  const [costs, setCosts] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/credits/costs");
      setCosts(r.data.costs || {});
      setDefaults(r.data.defaults || {});
    } catch { toast.error("Could not load costs"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateCost = async (feature, cost) => {
    setSaving(feature);
    try {
      await api.post("/admin/credits/costs", { feature, cost });
      setCosts((p) => ({ ...p, [feature]: cost }));
      toast.success(`${FEATURE_LABEL[feature] || feature}: ${cost} credit${cost === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  const resetCost = async (feature) => {
    setSaving(feature);
    try {
      const r = await api.delete(`/admin/credits/costs/${feature}`);
      setCosts((p) => ({ ...p, [feature]: r.data.cost }));
      toast.success(`Reset to default (${r.data.cost})`);
    } catch { toast.error("Reset failed"); }
    finally { setSaving(null); }
  };

  if (loading) {
    return <div className="py-10 text-center font-mono text-xs text-ink-muted">LOADING...</div>;
  }

  const keys = Object.keys(defaults).sort((a, b) => (defaults[b] || 0) - (defaults[a] || 0));

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="admin-costs">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-brand-cyan" />
          <h3 className="font-display text-xl font-bold">Feature costs</h3>
        </div>
        <div className="text-xs text-ink-muted">Changes apply to all customers immediately</div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-muted">
          <tr className="border-b border-line">
            <th className="py-2">Feature</th>
            <th className="text-right w-32">Current cost</th>
            <th className="text-right w-24">Default</th>
            <th className="text-right w-44">Actions</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const current = costs[k] ?? defaults[k];
            const def = defaults[k];
            const overridden = current !== def;
            return (
              <tr key={k} className="border-b border-line/30" data-testid={`admin-cost-row-${k}`}>
                <td className="py-2 text-ink-primary">{FEATURE_LABEL[k] || k}</td>
                <td className="text-right">
                  <input
                    type="number"
                    min={0}
                    value={current}
                    onChange={(e) => setCosts((p) => ({ ...p, [k]: parseInt(e.target.value || "0", 10) }))}
                    data-testid={`admin-cost-input-${k}`}
                    className={`w-20 px-2 py-1 rounded-md border bg-bg-elevated font-mono text-right outline-none ${
                      overridden ? "border-brand-amber text-brand-amber" : "border-line"
                    }`}
                  />
                </td>
                <td className="text-right font-mono text-ink-muted">{def}</td>
                <td className="text-right">
                  <button
                    onClick={() => updateCost(k, current)}
                    disabled={saving === k}
                    data-testid={`admin-cost-save-${k}`}
                    className="text-[11px] inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-2 py-1 hover:bg-brand-amber/20 transition disabled:opacity-50"
                  >
                    {saving === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Save
                  </button>
                  {overridden && (
                    <button
                      onClick={() => resetCost(k)}
                      data-testid={`admin-cost-reset-${k}`}
                      className="ml-1 text-[11px] inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 hover:text-ink-primary transition"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Annual nudge funnel tab — shown → clicked → upgraded
// ────────────────────────────────────────────────────────────
function NudgeTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/billing/annual-nudge-stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load nudge stats"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 text-center text-ink-muted"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!stats) return null;
  const f = stats.funnel;

  return (
    <div className="space-y-6" data-testid="nudge-tab">
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Banner shown", value: f.shown.users, sub: `${f.shown.events} user-days` },
          { label: "CTA clicked", value: f.clicked.users, sub: `${stats.click_rate_pct}% click rate` },
          { label: "Upgraded to yearly", value: f.upgraded.users, sub: `${stats.conversion_rate_pct}% conversion` },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-line bg-bg-surface p-5" data-testid={`nudge-stat-${s.label.split(" ")[1] || s.label}`}>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">// {s.label}</div>
            <div className="font-display text-4xl font-black">{s.value}</div>
            <div className="text-xs text-ink-secondary mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/5 p-5" data-testid="nudge-revenue">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-2">// ANNUAL REVENUE DRIVEN</div>
          <div className="font-display text-4xl font-black text-emerald-300">${stats.annual_revenue_usd.toLocaleString()}</div>
          <div className="text-xs text-ink-secondary mt-1">Sum of yearly plan value across nudge upgrades</div>
        </div>
        <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="nudge-savings">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">// CUSTOMER SAVINGS GIVEN</div>
          <div className="font-display text-4xl font-black">${stats.savings_given_usd.toLocaleString()}</div>
          <div className="text-xs text-ink-secondary mt-1">"2 months free" value passed to subscribers</div>
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface overflow-hidden">
        <div className="px-5 py-3 border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// RECENT UPGRADES</div>
        {stats.recent_upgrades.length === 0 ? (
          <div className="p-6 text-sm text-ink-muted" data-testid="nudge-upgrades-empty">No nudge-driven upgrades yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="nudge-upgrades-table">
            <tbody>
              {stats.recent_upgrades.map((u, i) => (
                <tr key={i} className="border-t border-line/50">
                  <td className="px-5 py-2.5 font-mono text-xs text-ink-secondary">{(u.ts || "").slice(0, 10)}</td>
                  <td className="px-5 py-2.5">{u.user_id}</td>
                  <td className="px-5 py-2.5 uppercase font-mono text-xs">{u.plan}</td>
                  <td className="px-5 py-2.5 text-right text-emerald-300">${u.yearly_usd}/yr</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
